const MODEL_PATH = '/ai/sface/models/face_recognition_sface_2021dec_int8.onnx';
const WASM_PATH = '/ai/sface/wasm/';
const FACE_SIZE = 112;
const TEMPLATE = [
  [38.2946, 51.6963],
  [73.5318, 51.5014],
  [56.0252, 71.7366],
  [41.5493, 92.3655],
  [70.7299, 92.2041],
];
let sessionPromise;

function solveSimilarityTransform(source, target = TEMPLATE) {
  const sourceMean = source.reduce((sum, point) => [sum[0] + point[0], sum[1] + point[1]], [0, 0]).map(value => value / source.length);
  const targetMean = target.reduce((sum, point) => [sum[0] + point[0], sum[1] + point[1]], [0, 0]).map(value => value / target.length);
  let denominator = 0;
  let scaleCos = 0;
  let scaleSin = 0;
  source.forEach((point, index) => {
    const sx = point[0] - sourceMean[0];
    const sy = point[1] - sourceMean[1];
    const tx = target[index][0] - targetMean[0];
    const ty = target[index][1] - targetMean[1];
    denominator += sx * sx + sy * sy;
    scaleCos += sx * tx + sy * ty;
    scaleSin += sx * ty - sy * tx;
  });
  if (denominator < 1e-6) throw new Error('人脸关键点不足以完成对齐');
  const a = scaleCos / denominator;
  const b = scaleSin / denominator;
  return {
    a,
    b,
    c: -b,
    d: a,
    e: targetMean[0] - a * sourceMean[0] + b * sourceMean[1],
    f: targetMean[1] - b * sourceMean[0] - a * sourceMean[1],
  };
}

function alignFace(imageSource, normalizedKeypoints) {
  const width = imageSource.width || imageSource.videoWidth || imageSource.naturalWidth;
  const height = imageSource.height || imageSource.videoHeight || imageSource.naturalHeight;
  const source = normalizedKeypoints.map(point => [point.x * width, point.y * height]);
  const transform = solveSimilarityTransform(source);
  const canvas = document.createElement('canvas');
  canvas.width = FACE_SIZE;
  canvas.height = FACE_SIZE;
  const context = canvas.getContext('2d', { willReadFrequently: true });
  context.setTransform(transform.a, transform.b, transform.c, transform.d, transform.e, transform.f);
  context.drawImage(imageSource, 0, 0);
  context.resetTransform();
  return canvas;
}

function canvasToTensorData(canvas) {
  const { data } = canvas.getContext('2d', { willReadFrequently: true }).getImageData(0, 0, FACE_SIZE, FACE_SIZE);
  const planeSize = FACE_SIZE * FACE_SIZE;
  const tensor = new Float32Array(planeSize * 3);
  for (let pixel = 0; pixel < planeSize; pixel += 1) {
    const source = pixel * 4;
    tensor[pixel] = data[source];
    tensor[planeSize + pixel] = data[source + 1];
    tensor[planeSize * 2 + pixel] = data[source + 2];
  }
  return tensor;
}

function normalizeEmbedding(values) {
  const embedding = Array.from(values, Number);
  const norm = Math.sqrt(embedding.reduce((sum, value) => sum + value * value, 0));
  return norm > 0 ? embedding.map(value => value / norm) : embedding;
}

async function getSFaceSession() {
  if (!sessionPromise) {
    sessionPromise = import('onnxruntime-web/wasm').then(async ort => {
      ort.env.wasm.wasmPaths = WASM_PATH;
      ort.env.wasm.numThreads = 1;
      ort.env.wasm.proxy = false;
      const session = await ort.InferenceSession.create(MODEL_PATH, {
        executionProviders: ['wasm'],
        graphOptimizationLevel: 'all',
      });
      return { ort, session };
    }).catch(error => {
      sessionPromise = undefined;
      throw error;
    });
  }
  return sessionPromise;
}

async function extractFaceEmbedding(imageSource, keypoints) {
  const aligned = alignFace(imageSource, keypoints);
  const { ort, session } = await getSFaceSession();
  const input = new ort.Tensor('float32', canvasToTensorData(aligned), [1, 3, FACE_SIZE, FACE_SIZE]);
  const output = await session.run({ data: input });
  return normalizeEmbedding(output.fc1.data);
}

export { FACE_SIZE, TEMPLATE, alignFace, extractFaceEmbedding, normalizeEmbedding, solveSimilarityTransform };
